import { Popover as HPopover } from "@headlessui/react"
import { ReactNode, useCallback, useState } from "react"
import { CheckMarkSvg } from "~/ui-rtk/components/ui/svg/essentials"
import { ChevronDownSvg } from "../../svg/arrow"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export type TMultiSelectOption = {
  label: string
  value: string
  /** Optional second line rendered small + muted under the label. */
  description?: string
  icon?: ReactNode | null
  disabled?: boolean
  color?: string
  isBeta?: boolean
}

export type TMultiSelectProps = {
  options: TMultiSelectOption[]
  value?: TMultiSelectOption[]
  defaultValue?: TMultiSelectOption[]
  className?: string
  classes?: Partial<{
    list: string
    button: string
    label: string
  }>
  label?: string
  placeholder?: string
  /**
   * Render the options panel in a body-level portal with fixed positioning,
   * so it overlays scroll/overflow containers (e.g. inside a modal) instead
   * of being clipped by them.
   */
  portal?: boolean
  onChange: (options: TMultiSelectOption[]) => void
}

export default function MultiSelect({
  options,
  value: valueProps,
  defaultValue,
  className,
  classes,
  label,
  placeholder,
  portal = false,
  onChange: onChangeFromProps,
}: TMultiSelectProps) {
  const isControlled = typeof valueProps !== "undefined"
  const hasDefaultValue = typeof defaultValue !== "undefined"

  const [internalValue, setInternalValue] = useState<TMultiSelectOption[]>(
    hasDefaultValue ? defaultValue : [],
  )

  const selectedOptions = isControlled ? valueProps : internalValue

  const onChange = (e: TMultiSelectOption[]) => {
    if (onChangeFromProps) {
      onChangeFromProps(e)
    }

    if (!isControlled) {
      setInternalValue(e)
    }
  }

  const getSelectedIdx = (option: TMultiSelectOption) =>
    selectedOptions.findIndex(_option => option.value === _option.value)
  const isOptionSelected = (option: TMultiSelectOption) =>
    getSelectedIdx(option) !== -1
  const toggle = useCallback(
    (option: TMultiSelectOption) => {
      if (option.disabled) {
        return
      }
      if (isOptionSelected(option)) {
        const idx = getSelectedIdx(option)
        const newSelected = [...selectedOptions]
        newSelected.splice(idx, 1)
        onChange(newSelected)
      } else {
        onChange([...selectedOptions, option])
      }
    },
    [selectedOptions],
  )

  let btnLabel
  if (label) {
    btnLabel = label
  } else if (placeholder) {
    btnLabel = placeholder
  } else if (selectedOptions.length > 0) {
    btnLabel = `Selected ${selectedOptions.length} item${selectedOptions.length > 1 ? "s" : ""}`
  } else {
    btnLabel = "Click to select"
  }

  const panelContent = (
    <div
      className={cn(
        "max-h-75 overflow-y-auto scrollbar container w-full text-white border rounded-md border-system-blue-600 bg-system-blue-700",
        classes?.list,
      )}
    >
      {options.map((option, idx) => {
        const isFirst = idx === 0
        const isLast = options.length - 1 === idx
        return (
          <div
            key={option.value}
            className={cn(
              `px-2 py-1.5 text-sm cursor-pointer flex items-center space-x-2 w-full pr-6 hover:bg-background-dark-shade ${option.disabled ? "disabled" : ""}`,
              isFirst && "rounded-t-md",
              isLast && "rounded-b-md",
            )}
            onClick={() => toggle(option)}
            aria-label={option.label}
          >
            {isOptionSelected(option) && (
              <CheckMarkSvg className="me-1" fill="green" />
            )}
            <div
              className={cn(
                `${!isOptionSelected(option) ? "ms-6" : ""}`,
                !isOptionSelected(option) && "pl-1",
                "whitespace-nowrap flex flex-col justify-center",
              )}
            >
              <div className="flex items-center justify-start gap-1">
                {option.icon && (
                  <div className="w-3 h-3 flex items-center justify-start">
                    {option.icon}
                  </div>
                )}
                {option.label}
              </div>
              {option.description && (
                <div className="text-[11px] text-system-gray-400">
                  {option.description}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <HPopover className={`relative ${className} ring-0 focus:ring-0 space-y-2`}>
      <HPopover.Button
        className={cn(
          "flex items-center w-full ring-0 h-12 focus:ring-0 outline-none justify-between gap-2 px-2 py-1.5 text-sm text-white border rounded-system-md border-system-blue-600 bg-system-blue-700",
          classes?.button,
        )}
      >
        <p className={cn("whitespace-nowrap", classes?.label)}>{btnLabel}</p>
        <ChevronDownSvg />
      </HPopover.Button>
      {portal ? (
        // `anchor` renders the panel in a body-level portal positioned by
        // Headless UI (floating-ui), so it overlays scroll/overflow containers
        // (e.g. modals) instead of being clipped by them.
        <HPopover.Panel
          anchor="bottom start"
          // z must beat the Dialog wrapper's z-[999999999] in case the portal
          // lands outside the dialog's stacking context.
          className="z-[1000000000] min-w-[var(--button-width)] [--anchor-gap:4px]"
        >
          {panelContent}
        </HPopover.Panel>
      ) : (
        <HPopover.Panel className="absolute z-40">
          {panelContent}
        </HPopover.Panel>
      )}
    </HPopover>
  )
}
